import { createContext, useContext, useEffect, useState } from "react";
import api, { clearTokens, formatApiError, readTokens, saveTokens } from "@/lib/api";

const AuthContext = createContext(null);

/** Drops the token fields before the user object reaches the UI. */
function toUser({ access_token, refresh_token, ...user }) {
  return user;
}

export function AuthProvider({ children }) {
  // A returning listener is shown their own app straight away, from the
  // record kept beside their tokens. /auth/me still runs and still has the
  // final word — it just no longer holds up the first paint, which cost
  // roughly two seconds of blank screen on every single load because the
  // page's own data could not even be requested until it answered.
  const [user, setUser] = useState(() => readTokens()?.user ?? null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        // No cookie and no usable token, treat the visitor as anonymous.
        if (!readTokens()) clearTokens();
        setUser(false);
      });
  }, []);

  // Presence heartbeat, for the "who is listening" panel in Settings.
  //
  // Every authenticated request already refreshes the account's last_seen, but
  // someone who presses play and puts the phone down makes no requests for the
  // length of an album. A hidden tab still counts as present while audio is
  // playing — that is the whole point of the app — which mediaSession already
  // tracks for the lock-screen controls, so nothing new has to be wired up.
  useEffect(() => {
    if (!user?.id) return undefined;

    let sentAt = 0;
    const ping = () => {
      const listening = navigator.mediaSession?.playbackState === "playing";
      if (document.visibilityState !== "visible" && !listening) return;
      // The server only writes once a minute; sending faster achieves nothing.
      if (Date.now() - sentAt < 55_000) return;
      sentAt = Date.now();
      api.post("/auth/ping").catch(() => {
        // Presence is decoration. A failed heartbeat is not worth a toast.
      });
    };

    ping();
    const timer = setInterval(ping, 60_000);
    document.addEventListener("visibilitychange", ping);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", ping);
    };
  }, [user?.id]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    saveTokens(data);
    const me = toUser(data);
    setUser(me);
    return me;
  };

  const register = async (name, email, password, inviteCode) => {
    const { data } = await api.post("/auth/register", {
      name,
      email,
      password,
      invite_code: inviteCode,
    });
    saveTokens(data);
    const me = toUser(data);
    setUser(me);
    return me;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* the session is being discarded either way */
    }
    clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
